import { useEffect, useState } from 'react';

/**
 * Desktop breakpoint gate. COYG's CSS programme-spread already switches at
 * `min-width: 1024px`; this hook gives the same signal to width-gated JSX
 * (matches master-detail) so the NARROW DOM stays byte-for-byte the current
 * mobile tree. Defaults to false (mobile) when matchMedia is unavailable — so
 * the test DOM and any SSR path render the unchanged narrow markup.
 */
const QUERY = '(min-width: 1024px)';

function read(): boolean {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(QUERY).matches;
  } catch {
    return false;
  }
}

export function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(QUERY);
    } catch {
      return;
    }
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return matches;
}
